// src/pages/EliteDashboard.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import API from "../utils/api";
import FloatingNotice from "../components/FloatingNotice";
import ReferralWithdrawalCard from "../components/ReferralWithdrawalCard";
import { useCountry } from "../context/CountryContext";

import opayLogo from "../assets/opay.png";
import palmpayLogo from "../assets/palmpay.png";
import kudaLogo from "../assets/kuda.png";
import moniepointLogo from "../assets/moniepoint.png";
import binanceLogo from "../assets/binance.png";
import bybitLogo from "../assets/bybit.png";
import lunoLogo from "../assets/luno.png";
import paypalLogo from "../assets/paypal.png";

const TELEGRAM_BOT_URL = "https://t.me/probanksupport_bot";
const TAWK_SRC = "https://embed.tawk.to/68deec7532ab2019572628de/1j6jcnbu7";

const EliteBadge = () => (
  <motion.span
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    className="relative inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-full text-black overflow-hidden"
    style={{ background: "linear-gradient(135deg, #FFD700, #FFA500, #FFD700)" }}
  >
    <span className="relative z-10">👑 Elite Plan</span>
    <motion.span
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent"
      animate={{ x: ["-100%", "200%"] }}
      transition={{ repeat: Infinity, duration: 2.5, ease: "linear" }}
    />
  </motion.span>
);

const EliteStatCard = ({ label, value, icon }) => (
  <div className="p-4 rounded-2xl bg-gradient-to-br from-yellow-500/10 to-black/40 border border-yellow-500/20 backdrop-blur-md shadow-lg">
    <div className="flex items-center gap-2 mb-1">
      <span className="text-lg">{icon}</span>
      <p className="text-xs text-yellow-200/60 uppercase tracking-wide">{label}</p>
    </div>
    <p className="text-xl font-bold text-yellow-100">{value}</p>
  </div>
);

const TelegramBotBanner = ({ onDismiss }) => (
  <motion.div
    initial={{ opacity: 0, y: -10 }}
    animate={{ opacity: 1, y: 0 }}
    className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-yellow-500/12 via-yellow-500/5 to-transparent border border-yellow-500/25 flex items-center justify-between gap-3 flex-wrap backdrop-blur-md"
  >
    <div className="flex items-start gap-3">
      <span className="text-2xl">🤖</span>
      <div>
        <p className="font-semibold text-white text-sm">Need help? Chat with our Telegram assistant</p>
        <p className="text-xs text-yellow-100/50 mt-0.5">
          Register, check your balance, buy coins, and get answers instantly.
        </p>
      </div>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <a
        href={TELEGRAM_BOT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="px-4 py-2 rounded-lg text-sm font-bold text-black transition hover:brightness-110 shadow-lg whitespace-nowrap"
        style={{ background: "linear-gradient(135deg, #FFD700, #FFA500)" }}
      >
        Open Telegram Bot
      </a>
      <button onClick={onDismiss} className="text-yellow-100/40 hover:text-white text-lg leading-none px-1" aria-label="Dismiss">✕</button>
    </div>
  </motion.div>
);

const TawkFloatingButton = () => {
  const [statusMsg, setStatusMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const loadTawkAndOpen = () => {
    if (window.Tawk_API && typeof window.Tawk_API.maximize === "function") {
      window.Tawk_API.showWidget();
      window.Tawk_API.maximize();
      return;
    }
    if (document.getElementById("tawk-elite-script")) {
      setStatusMsg("Loading support chat…");
      setTimeout(() => {
        if (window.Tawk_API?.maximize) {
          window.Tawk_API.showWidget();
          window.Tawk_API.maximize();
          setStatusMsg("");
        } else {
          setStatusMsg("Still loading — try again in a moment.");
          setTimeout(() => setStatusMsg(""), 3000);
        }
      }, 1500);
      return;
    }

    setLoading(true);
    setStatusMsg("Loading support chat…");
    const s1 = document.createElement("script");
    s1.id = "tawk-elite-script";
    s1.async = true;
    s1.src = TAWK_SRC;
    s1.charset = "UTF-8";
    s1.setAttribute("crossorigin", "*");
    s1.onload = () => {
      let attempts = 0;
      const check = setInterval(() => {
        attempts++;
        if (window.Tawk_API?.maximize) {
          clearInterval(check);
          window.Tawk_API.showWidget();
          window.Tawk_API.maximize();
          setStatusMsg("");
          setLoading(false);
        } else if (attempts > 10) {
          clearInterval(check);
          setStatusMsg("Couldn't open support chat — try again.");
          setTimeout(() => setStatusMsg(""), 4000);
          setLoading(false);
        }
      }, 300);
    };
    s1.onerror = () => {
      setStatusMsg("Couldn't load support chat. Check your connection.");
      setTimeout(() => setStatusMsg(""), 4000);
      setLoading(false);
    };
    document.body.appendChild(s1);
  };

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {statusMsg && (
        <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-black/90 border border-yellow-500/20 text-white text-xs rounded-lg whitespace-nowrap shadow-lg max-w-xs">
          {statusMsg}
        </div>
      )}
      <button
        onClick={loadTawkAndOpen}
        disabled={loading}
        className="px-5 py-3 text-black font-bold rounded-full shadow-2xl flex items-center gap-2 transition transform active:scale-95 disabled:opacity-60"
        style={{ background: "linear-gradient(135deg, #FFD700, #FFA500)" }}
      >
        {loading ? "Loading…" : "Chat Support"}
      </button>
    </div>
  );
};

const TicketHistoryCard = () => {
  const [myTickets, setMyTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/support/my-tickets")
      .then((res) => setMyTickets(res.data))
      .catch((err) => console.error("Fetch my-tickets error:", err.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (myTickets.length === 0) return null;

  return (
    <div className="p-5 rounded-2xl bg-gradient-to-br from-yellow-500/5 to-black/40 border border-yellow-500/15 backdrop-blur-md shadow-xl">
      <h3 className="text-sm font-semibold text-yellow-100/70 mb-3">Your Tickets</h3>
      <div className="space-y-3 max-h-72 overflow-y-auto">
        {myTickets.map((t) => (
          <div key={t._id} className="p-3 rounded-lg bg-black/30 border border-yellow-500/10">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-yellow-100">{t.subject}</p>
              <span
                className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${
                  t.status === "resolved" ? "bg-emerald-600 text-white" : "bg-yellow-600 text-black"
                }`}
              >
                {t.status}
              </span>
            </div>
            <p className="text-xs text-yellow-100/40 mb-1">{new Date(t.createdAt).toLocaleDateString()}</p>
            {t.adminReply && (
              <div className="mt-2 p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-[10px] text-emerald-300 font-semibold mb-0.5">Team reply:</p>
                <p className="text-xs text-emerald-100">{t.adminReply}</p>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function EliteDashboard() {
  const { isGhana, config } = useCountry();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [notice, setNotice] = useState("");
  const [telegramBannerVisible, setTelegramBannerVisible] = useState(true);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketPriority, setTicketPriority] = useState("normal");
  const [ticketMessage, setTicketMessage] = useState("");
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  // ✅ NEW: surfaces a real fetch failure instead of hanging on
  // "LOADING ELITE DASHBOARD" forever with no visible cause.
  const [loadError, setLoadError] = useState("");

  const currencySymbol = config.currencySymbol;
  const flag = isGhana ? "🇬🇭" : "🇳🇬";

  const wallets = isGhana
    ? [
        { name: "MTN MoMo", emoji: "📱", link: "/wallet", active: true },
        { name: "Telecel Cash", emoji: "💳", link: "/wallet-palmpay", active: true },
        { name: "AirtelTigo", emoji: "💰", link: "/wallet-kuda", active: true },
        { name: "GCB Bank", emoji: "🏦", link: "/moniepoint", active: true },
      ]
    : [
        { name: "Opay", logo: opayLogo, link: "/wallet", active: true },
        { name: "PalmPay", logo: palmpayLogo, link: "/wallet-palmpay", active: true },
        { name: "Kuda", logo: kudaLogo, link: "/wallet-kuda", active: true },
        { name: "MoniePoint", logo: moniepointLogo, link: "/moniepoint", active: true },
      ];

  const cryptoWallets = [
    { name: "Binance", logo: binanceLogo, active: false },
    { name: "Bybit", logo: bybitLogo, active: false },
    { name: "Luno", logo: lunoLogo, active: true, link: "/luno-dashboard" },
    { name: "PayPal", logo: paypalLogo, active: false },
  ];

  const fetchDashboard = async () => {
    try {
      const res = await API.get("/dashboard");
      setData(res.data);
      setLoadError("");
      if (res.data.user) localStorage.setItem("user", JSON.stringify(res.data.user));
    } catch (err) {
      console.error("Dashboard fetch error:", err);
      setLoadError(
        `${err.response?.status || "?"} ${err.response?.data?.message || err.message}`
      );
    }
  };

  useEffect(() => {
    fetchDashboard();
    const interval = setInterval(fetchDashboard, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(`https://prorealalert.com/register?ref=${data.user?.referralCode || ""}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const showComingSoon = (name) => {
    setNotice(`${name} is coming soon!`);
  };

  const submitTicket = async () => {
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      setNotice("Please fill in subject and message");
      return;
    }
    setTicketSubmitting(true);
    try {
      await API.post("/support/ticket", {
        subject: ticketSubject,
        priority: ticketPriority,
        message: ticketMessage,
      });
      setNotice("✅ VIP ticket submitted — our team will respond shortly");
      setTicketSubject("");
      setTicketMessage("");
      setTicketPriority("normal");
      setTicketModalOpen(false);
    } catch (err) {
      setNotice("Failed to submit ticket — try WhatsApp instead");
    } finally {
      setTicketSubmitting(false);
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black p-6">
        <div className="text-center max-w-md">
          <p className="text-yellow-200/60 text-sm tracking-wide animate-pulse mb-4">LOADING ELITE DASHBOARD</p>
          {loadError && (
            <div className="mt-2 p-3 rounded-lg bg-red-500/10 border border-red-500/40 text-red-300 text-xs text-left break-words">
              🔬 Debug: {loadError}
            </div>
          )}
        </div>
      </div>
    );
  }

  if (data.user?.accountLocked) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-black text-white">
        <div className="text-center">
          <h2 className="text-xl font-bold text-red-400">Account Locked</h2>
          <p className="mt-2 text-yellow-200/50">{data.user.accountLockReason}</p>
        </div>
      </div>
    );
  }

  const bankWallets = wallets.map((w) => ({ ...w, balance: data.user?.balance || 0 }));

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#1a1005] to-black text-white px-5 py-8 md:px-10 md:py-10 relative overflow-hidden">
      <div className="pointer-events-none fixed -top-32 -right-32 w-[550px] h-[550px] rounded-full bg-yellow-500/8 blur-[140px]" />
      <div className="pointer-events-none fixed -bottom-32 -left-32 w-[400px] h-[400px] rounded-full bg-amber-600/8 blur-[140px]" />

      <div className="fixed top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 border border-yellow-500/20 backdrop-blur-md">
        <span className="text-sm leading-none">{flag}</span>
        <span className="text-xs text-yellow-100/60 font-medium">{config.name}</span>
      </div>

      <div className="max-w-6xl mx-auto relative">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
          <h1 className="text-3xl font-bold">
            Hello, <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #FFD700, #FFA500)" }}>{data.user?.name || "User"}</span>
          </h1>
          <EliteBadge />
        </div>
        <p className="text-sm text-yellow-100/40 mb-6">International support, priority service, everything in Premium.</p>

        {telegramBannerVisible && <TelegramBotBanner onDismiss={() => setTelegramBannerVisible(false)} />}

        {data.user?.isRestricted && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
            ⚠️ {data.user.customMessage || "Your account is limited"}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-3xl p-8 mb-6 overflow-hidden bg-gradient-to-br from-yellow-500/12 via-black/60 to-amber-900/20 border border-yellow-500/25 shadow-2xl"
        >
          <div className="absolute top-0 right-0 w-72 h-72 rounded-full bg-yellow-500/10 blur-3xl -mr-24 -mt-24" />
          <div className="relative">
            <p className="text-xs uppercase tracking-widest text-yellow-200/50 mb-2 font-semibold">Available Balance</p>
            <p
              className="text-5xl font-extrabold text-transparent bg-clip-text mb-3"
              style={{ backgroundImage: "linear-gradient(135deg, #FFD700, #FFA500, #FFD700)" }}
            >
              {currencySymbol}{data.user?.balance?.toLocaleString() || "0"}
            </p>
            <p className="text-sm text-yellow-100/40">
              Withdrawable: <span className="text-yellow-100 font-medium">{currencySymbol}{data.user?.withdrawableBalance?.toLocaleString() || 0}</span>
            </p>

            <div className="flex flex-wrap gap-3 mt-6">
              <button
                onClick={handleCopy}
                className="px-5 py-2.5 rounded-xl font-semibold text-black shadow-lg hover:brightness-110 transition"
                style={{ background: "linear-gradient(135deg, #FFD700, #FFA500)" }}
              >
                {copied ? "✓ Copied" : "Copy Referral"}
              </button>
              <button
                onClick={() => navigate("/purchase")}
                className="px-5 py-2.5 rounded-xl font-semibold bg-white/10 hover:bg-white/15 border border-white/10 transition backdrop-blur-sm"
              >
                + Add Money
              </button>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* ✅ FIXED: was referralsCount / referralBonus (dead fields, always 0) */}
          <EliteStatCard icon="🎯" label="Referrals" value={data.user?.referralCount || 0} />
          <EliteStatCard icon="💎" label="Bonus Earned" value={`${currencySymbol}${data.user?.referralEarnings || 0}`} />
          <EliteStatCard icon="🌍" label="Plan" value="Elite" />
          <EliteStatCard icon="⚡" label="Support" value="Priority" />
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-6">
          <div className="md:col-span-2 p-6 rounded-2xl bg-gradient-to-br from-yellow-500/5 to-black/40 border border-yellow-500/15 backdrop-blur-md shadow-xl">
            <h3 className="font-semibold text-lg mb-1">Wallets</h3>
            <p className="text-xs text-yellow-100/40 mb-5">Access your bank and crypto wallets</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {bankWallets.map((w, i) => (
                <motion.button
                  key={i}
                  whileHover={{ scale: 1.04 }}
                  onClick={() => (w.active && w.link ? navigate(w.link) : showComingSoon(w.name))}
                  className="p-4 rounded-2xl bg-black/40 border border-yellow-500/10 hover:border-yellow-500/30 flex flex-col items-center shadow-lg transition"
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-yellow-900/40 to-black flex items-center justify-center overflow-hidden mb-2 shadow-inner">
                    {w.logo ? <img src={w.logo} alt={w.name} className="w-full h-full object-contain" /> : <span className="text-2xl">{w.emoji}</span>}
                  </div>
                  <div className="text-xs font-bold text-yellow-200">{w.name}</div>
                  <div className="text-xs text-yellow-100/40 mt-0.5">{currencySymbol}{(w.balance || 0).toLocaleString()}</div>
                </motion.button>
              ))}
            </div>

            <div className="mt-5 pt-5 border-t border-yellow-500/10">
              <p className="text-xs font-semibold text-yellow-100/40 mb-3">Crypto Wallets</p>
              <div className="flex flex-wrap gap-2">
                {cryptoWallets.map((w, i) => (
                  <button
                    key={i}
                    onClick={() => (w.active && w.link ? navigate(w.link) : showComingSoon(w.name))}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-black/40 border border-yellow-500/10 hover:border-yellow-500/30 transition"
                  >
                    <img src={w.logo} alt={w.name} className="w-5 h-5" />
                    <span className="text-xs font-semibold text-yellow-200/80">{w.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar — VIP support, ticket history, referral + withdrawal */}
          <div className="space-y-5">
            <div className="p-5 rounded-2xl bg-gradient-to-br from-yellow-500/10 to-black/50 border border-yellow-500/20 backdrop-blur-md shadow-xl">
              <h3 className="text-sm font-semibold text-yellow-200 mb-3">👑 VIP Support</h3>
              <p className="text-xs text-yellow-100/50 mb-4">Priority response, dedicated to Elite members.</p>
              <button
                onClick={() => setTicketModalOpen(true)}
                className="w-full py-2.5 rounded-xl font-semibold text-black shadow-lg hover:brightness-110 transition mb-2"
                style={{ background: "linear-gradient(135deg, #FFD700, #FFA500)" }}
              >
                Submit VIP Ticket
              </button>
              <a
                href="https://wa.me/27652611261"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center py-2.5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition text-sm text-yellow-100/70"
              >
                💬 VIP WhatsApp Line
              </a>
            </div>

            <TicketHistoryCard />

            {/* ✅ NEW: was completely missing on Elite — same gap as Basic/Premium. */}
            <ReferralWithdrawalCard
              currencySymbol={currencySymbol}
              theme={{
                card: "bg-gradient-to-br from-yellow-500/5 to-black/40 border border-yellow-500/15 backdrop-blur-md shadow-xl",
                accent: "text-black",
              }}
            />

            <div className="p-5 rounded-2xl bg-gradient-to-br from-yellow-500/5 to-black/40 border border-yellow-500/15 backdrop-blur-md shadow-xl">
              <h3 className="text-sm font-semibold text-yellow-100/70 mb-3">Referral Link</h3>
              <p className="text-xs text-pink-300 font-mono mb-2">{data.user?.referralCode || "N/A"}</p>
              <p className="text-[11px] text-yellow-100/30 break-all">
                https://prorealalert.com/register?ref={data.user?.referralCode || ""}
              </p>
            </div>
          </div>
        </div>
      </div>

      {notice && <FloatingNotice message={notice} />}
      <TawkFloatingButton />

      <AnimatePresence>
        {ticketModalOpen && (
          <motion.div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-[70] p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              className="w-full max-w-md rounded-2xl p-6 bg-gradient-to-br from-[#1a1005] to-black border border-yellow-500/20 shadow-2xl"
            >
              <h3 className="text-lg font-bold text-yellow-200 mb-4">👑 Submit VIP Ticket</h3>

              <label className="text-xs text-yellow-100/50 mb-1 block">Subject</label>
              <input
                type="text"
                value={ticketSubject}
                onChange={(e) => setTicketSubject(e.target.value)}
                className="w-full mb-3 p-2.5 rounded-lg bg-black/60 border border-yellow-500/20 text-white text-sm outline-none focus:border-yellow-500/50"
                placeholder="Brief subject"
              />

              <label className="text-xs text-yellow-100/50 mb-1 block">Priority</label>
              <div className="flex gap-2 mb-3">
                {["normal", "high", "urgent"].map((p) => (
                  <button
                    key={p}
                    onClick={() => setTicketPriority(p)}
                    className={`flex-1 py-2 rounded-lg text-xs font-medium capitalize transition ${
                      ticketPriority === p
                        ? "text-black"
                        : "bg-white/5 text-yellow-100/50 border border-white/10"
                    }`}
                    style={ticketPriority === p ? { background: "linear-gradient(135deg, #FFD700, #FFA500)" } : {}}
                  >
                    {p}
                  </button>
                ))}
              </div>

              <label className="text-xs text-yellow-100/50 mb-1 block">Message</label>
              <textarea
                value={ticketMessage}
                onChange={(e) => setTicketMessage(e.target.value)}
                rows={4}
                className="w-full mb-4 p-2.5 rounded-lg bg-black/60 border border-yellow-500/20 text-white text-sm outline-none focus:border-yellow-500/50"
                placeholder="Describe your issue..."
              />

              <div className="flex gap-3">
                <button
                  onClick={() => setTicketModalOpen(false)}
                  className="flex-1 py-2.5 rounded-lg bg-white/5 border border-white/10 text-yellow-100/70 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={submitTicket}
                  disabled={ticketSubmitting}
                  className="flex-1 py-2.5 rounded-lg font-semibold text-black text-sm disabled:opacity-50"
                  style={{ background: "linear-gradient(135deg, #FFD700, #FFA500)" }}
                >
                  {ticketSubmitting ? "Submitting..." : "Submit Ticket"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
      }
